import { Link, useLocation } from "react-router-dom";
import useBreadcrumbs from "use-react-router-breadcrumbs";
import { usePermissions } from "../../context/permissionsContext";
import { LEVEL_ADMIN, LEVEL_GUEST } from "../../utils/userPermissions";
import { CHATROOM } from "../../utils/globalConstants";
import { textCapitalize } from "../../utils/generalHelper";
import { useSelector } from "react-redux";
import { getCurrentUser } from "../../redux/selectors";

const routes = [
  {
    path: "/",
    breadcrumb: null,
  },
  {
    path: "/dashboard",
    breadcrumb: "Home",
  },
  {
    path: "/answer",
    breadcrumb: null,
  },
  {
    path: "/answer/:user_id",
    breadcrumb: "Answer",
  },
  {
    path: "/profile",
    breadcrumb: null,
  },
  {
    path: "/profile/:user_id",
    breadcrumb: "Profile",
  },
  {
    path: "/course-categories/:course_id",
    breadcrumb: null,
  },
  {
    path: "/course-categories/courses/:lesson_id",
    breadcrumb: null,
  },
  {
    path: "/admin/courses/:course_id",
    breadcrumb: null,
  },
  {
    path: "/user-notification/:uuid",
    breadcrumb: null,
  },
  {
    path: "/courses/:courseId/lessons",
    breadcrumb: "Lessons",
  },
  {
    path: "/courses/:courseId/lessons/:lessonId",
    breadcrumb: "Lessons",
  },
  {
    path: "/launch-process",
    breadcrumb: "Launch Process",
  },
  {
    path: "/launch-process/challenges/view",
    breadcrumb: "Launch Process",
  },
  {
    path: "/launch-process/challenges/create",
    breadcrumb: "Create Challenge",
  },
  {
    path: "/launch-process/challenges/edit/:uuid",
    breadcrumb: "Edit Challenge",
  },
  {
    path: "/launch-process/challenges/:uuid/responses",
    breadcrumb: "Challenge Responses",
  },
  {
    path: "/launch-process/leaderboard",
    breadcrumb: "Leaderboard",
  },
  {
    path: "/challenges",
    breadcrumb: "Challenges",
  },
  {
    path: "/challenges/listing",
    breadcrumb: "Challenges",
  },
  {
    path: "/challenges/create",
    breadcrumb: "Create",
  },
  {
    path: "/challenges/edit/:uuid",
    breadcrumb: "Edit Challenge",
  },
];

const Header = () => {
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs(routes);
  const { hasPermission } = usePermissions();
  const communityName = location.search.split("&")[2] || "";
  const currentUser = useSelector((state) => getCurrentUser(state));

  return (
    <div
      className={`header bg-primary pb-6 ${
        location.pathname.includes(
          "/affiliate-program/initiate-withdraw/w9-form"
        ) && "d-none"
      }`}
    >
      <div className="container-fluid">
        <div className="header-body">
          <div className="row align-items-center py-4 justify-content-center pt-2">
            <div className="col-lg-6 col-6">
              <h2 className="h2 d-inline-block mb-0 authenticated-heading text-nowrap text-capitalize">
                {location.pathname.includes("community") &&
                location.search.includes(CHATROOM)
                  ? textCapitalize(communityName.split("name=")[1])
                  : location.pathname.includes("/launch-process/challenges/edit/")
                  ? "Edit Challenge"
                  : breadcrumbs[breadcrumbs.length - 1]?.breadcrumb}
              </h2>
              <nav
                aria-label="breadcrumb"
                className="d-none d-md-inline-block ml-md-4"
              >
                <ol className="breadcrumb breadcrumb-links breadcrumb-dark">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard" className="custom-text-dark">
                      <i className="fa fa-home" />
                      &nbsp;&nbsp;-&nbsp;&nbsp;Home
                    </Link>
                  </li>
                  {location.pathname !== "/dashboard" && (
                    <li className="breadcrumb-item">
                      <Link to="/dashboard" className="custom-text-dark">
                        Dashboard
                      </Link>
                    </li>
                  )}
                  {breadcrumbs
                    .filter(({ match }) => match.pathname !== "/dashboard")
                    .map(({ match, breadcrumb }, index, array) => {
                      const uuidRegex =
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                      const pathParts = match.pathname.split("/");
                      const isEditPage = location.pathname.includes("/launch-process/challenges/edit/");
                      const isLastItem = index === array.length - 1;
                      const isEditChallengeBreadcrumb = breadcrumb === "Edit Challenge";

                      // If this path ends with a UUID and has "lessons" before it → show "Lessons"
                      if (
                        pathParts.length > 2 &&
                        pathParts[pathParts.length - 2] === "lessons" &&
                        uuidRegex.test(pathParts[pathParts.length - 1])
                      ) {
                        return (
                          <span
                            key={match.pathname}
                            className={
                              match.pathname === location.pathname
                                ? "breadcrumb-item active"
                                : "breadcrumb-item custom-text-dark"
                            }
                          >
                            Lessons
                          </span>
                        );
                      }

                      // Special handling for launch-process edit page
                      if (isEditPage) {
                        // Hide "Edit Challenge" from breadcrumb since it's already in the title
                        if (isEditChallengeBreadcrumb) {
                          return null;
                        }
                        // Hide UUID parts
                        if (pathParts.some((part) => uuidRegex.test(part))) {
                          return null;
                        }
                      }

                      // For any other pure UUID path → hide it
                      if (pathParts.some((part) => uuidRegex.test(part))) {
                        return null;
                      }

                      // Admin handling
                      if (
                        match.pathname === "/admin" ||
                        match.pathname === "/admin/guest-user"
                      ) {
                        return (
                          <span
                            key={match.pathname}
                            className="breadcrumb-item text-default"
                          >
                            {breadcrumb}
                          </span>
                        );
                      }

                      // Normal case
                      return (
                        <Link
                          key={match.pathname}
                          to={match.pathname}
                          className={
                            match.pathname === location.pathname
                              ? "breadcrumb-item active"
                              : "breadcrumb-item custom-text-dark"
                          }
                        >
                          {breadcrumb}
                        </Link>
                      );
                    })}
                </ol>
              </nav>
            </div>
            <div className="col-lg-5 col-5 text-right"></div>
            <div className="col-lg-12 pl-lg-5">
              {location.pathname === "/dashboard" && (
                <span className="breadcrumb breadcrumb-links breadcrumb-dark ml-lg-3">
                  Welcome to Race to Freedom, <b>{currentUser.name}</b>
                </span>
              )}
              {(location.pathname === "/courses" ||
                location.pathname === "/dashboard") &&
                hasPermission(LEVEL_GUEST) &&
                !hasPermission(LEVEL_ADMIN) && (
                  <span className="breadcrumb breadcrumb-links breadcrumb-dark ml-lg-3">
                    To unlock all courses and maximize your learning potential,
                    <Link
                      to="/account/payment"
                      style={{ color: "#32325a", textDecoration: "underline" }}
                    >
                      select a suitable plan now
                    </Link>
                  </span>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Header;
